'use client';

import {
	filterAccordionContentCSS,
	filterAccordionIconCSS,
	filterAccordionSelectedCountCSS,
	filterAccordionSummaryCSS,
} from '@/sections/filter/filter.css';
import { chevronIcon } from '@/uiIcons/chevronIcon.css';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { box } from '@/uiPrimitives/layout/box';
import { indentSize } from '@/uiPrimitives/layout/indentSize.definitions';
import { occupy } from '@/uiPrimitives/layout/occupy';
import { shelf } from '@/uiPrimitives/layout/shelf';
import { textBlock, textStyle } from '@/uiPrimitives/typography/text';
import { stack } from '@/uiPrimitives/layout/stack';
import { icon } from '@/uiPrimitives/media/icon';
import {
	FilterDetails,
	FilterSelectedOptionCount,
} from '@/sections/filter/FilterDetails';
import { addTestSelector } from '@/globals/testHelpers/testSelector';
import { checkboxCSS } from '@/uiComposites/form/checkbox.css';
import { useId } from 'react';
import { FilterClearToggle } from '@/sections/filter/FilterClearToggle';
import { FilterCheckbox } from '@/sections/filter/FilterCheckbox';
import {
	filterGroups,
	AmendmentFilterOption,
	AmendmentFilters,
	amendmentFilterQueryKeys,
} from './amendmentFilterKeys';
import { translateInline } from '@/globals/translate/translateInline';
import { WebAPILink } from '@/globals/webApiTypes/elements/WebAPILink';
import { ChevronLink } from '@/uiComposites/interactive/IconLink';
import { H2TextBlock } from '@/uiComposites/typography/headings';
import { WebAPIAmendmentsPage } from '@/globals/webApiTypes/pages/WebAPIAmendmentsPage';

export const FilterOptions = ({
	options,
	groupKey,
	selectAllText,
	className,
}: {
	options: Array<AmendmentFilterOption>;
	groupKey: string;
	selectAllText?: string;
	className?: string;
}) => {
	const baseID = useId();
	return (
		<div className={classes(stack({ gap: '2 | h2' }), className)}>
			<FilterClearToggle groupKey={groupKey}>{selectAllText}</FilterClearToggle>

			{options.map((option) => {
				const id = `${baseID}-${option.key}`;

				return (
					<div
						key={option.key}
						className={classes(
							shelf({
								gridTemplateColumns: 'auto minmax(0, 1fr)',
								alignItems: 'start',
							}),
						)}
						{...addTestSelector(`uiFilterCheckbox-${option.key}`)}
					>
						<span
							className={classes(
								occupy({
									inlineSize: indentSize,
									blockSize: 'capSize',
									justifyItems: 'start',
								}),
							)}
						>
							<FilterCheckbox
								id={id}
								name={id}
								optionKey={option.key}
								groupKey={groupKey}
								className={classes(checkboxCSS)}
							/>
						</span>
						<label
							htmlFor={id}
							className={classes(
								textBlock({
									color: 'positive',
								}),
							)}
						>
							{option.name}
						</label>
					</div>
				);
			})}
		</div>
	);
};

export const AmendmentsFilter = ({
	filters,
	className,
	promos,
}: {
	filters: AmendmentFilters;
	className?: string;
	promos: WebAPIAmendmentsPage['promos'];
}) => {
	return (
		<div className={classes(className, stack({ gap: '8 | betweenCards' }))}>
			<div role="group" className={stack({ gap: '2 | h2' })}>
				<div
					className={classes(
						textStyle({
							capSize: '-1 | small',
						}),
						box({
							borderBlockStartStyle: 'solid',
							borderColor: 'positiveMuted',
							borderWidth: '1px',
						}),
					)}
				>
					{filterGroups.map((filterGroup) => {
						const options = filters[filterGroup.key];

						if (options.length === 0) {
							return null;
						}

						return (
							<FilterDetails
								key={filterGroup.key}
								groupKey={amendmentFilterQueryKeys[filterGroup.queryKeyRef]}
								className={classes(
									box({
										borderBlockEndStyle: 'solid',
										borderColor: 'positiveMuted',
										borderWidth: '1px',
									}),
								)}
								{...addTestSelector(`uiFilter-${filterGroup.key}`)}
							>
								<summary
									className={classes(
										shelf({
											gridTemplateColumns: 'minmax(0, 1fr) auto',
										}),
										box({
											paddingBlock: '1 | h3',
										}),
										textStyle({
											fontWeight: 'bold',
											color: 'positive',
										}),
									)}
								>
									<span
										className={classes(
											textBlock({
												textWrap: 'balance',
											}),
											filterAccordionSummaryCSS,
										)}
									>
										{filterGroup.label}
										<FilterSelectedOptionCount
											className={filterAccordionSelectedCountCSS}
											groupKey={
												amendmentFilterQueryKeys[filterGroup.queryKeyRef]
											}
										/>
									</span>
									<span
										className={classes(
											occupy({
												inlineSize: indentSize,
												blockSize: 'capSize',
												justifyItems: 'end',
												alignItems: 'end',
											}),
										)}
									>
										<i
											className={classes(
												icon({
													icon: chevronIcon,
													adjustIcon: 'rotate90deg',
													blockSize: '1ex',
												}),
												filterAccordionIconCSS,
											)}
										/>
									</span>
								</summary>
								<FilterOptions
									options={options}
									className={classes(filterAccordionContentCSS)}
									groupKey={amendmentFilterQueryKeys[filterGroup.queryKeyRef]}
									selectAllText={filterGroup.selectAllText}
								/>
							</FilterDetails>
						);
					})}
				</div>
			</div>

			<div
				className={classes(
					box({
						paddingBlock: '1 | h3',
						paddingInline: '-2 | smaller',
						borderStyle: 'solid',
						borderColor: 'negativeBlue',
						borderWidth: '2px',
					}),
				)}
			>
				{promos.quarterExcerpts?.linkList != null && (
					<div
						className={classes(stack({ rowGap: '4 | betweenListItems' }))}
						{...addTestSelector('uiPromoblock-Actueel')}
					>
						<div className={classes(textStyle({ color: 'positiveMuted' }))}>
							{translateInline({ en: 'Current', nl: 'Actueel' })}
						</div>
						<H2TextBlock size="h3">
							{promos.quarterExcerpts.subTitle}
						</H2TextBlock>
						{promos.quarterExcerpts.linkList.map(
							(link: WebAPILink, i: number) => (
								<ChevronLink
									href={link.href}
									data-link-type="promoBlockLink"
									key={`${link.text}-${i}`}
								>
									{link.text}
								</ChevronLink>
							),
						)}
					</div>
				)}
			</div>
		</div>
	);
};
